import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetchUnchecked } from "./client.js";

import type {
  FinaliseProjectDocument,
  Project,
  ProjectCreate,
  ProjectCreateInput,
  ProjectDiscoveryBrief,
  ProjectDiscoveryBriefCreate,
  ProjectDiscoveryBriefListResponse,
  ProjectDiscoveryBriefUpdate,
  ProjectDocument,
  ProjectDocumentListResponse,
  RelatedProject,
  Task,
  TaskCategory,
  TaskComment,
  TaskCommentCreate,
  TaskCreate,
  TasksByCategoryResponse,
  StakeholderUpdateCreate,
  Workspace,
} from "@l30/shared";

const PROJECTS_KEY = ["projects"] as const;
const TASKS_KEY = ["tasks"] as const;

type ProjectListItem = Project & { taskCount: number; tasksDone: number };

/**
 * `undefined` — omit X-Tenant (host / staff default).
 * `null` — partner not chosen yet; queries stay disabled.
 * string — force that partner's schema.
 */
function tenantOpts(tenantSlug: string | null | undefined) {
  return typeof tenantSlug === "string" && tenantSlug.length > 0
    ? { tenantOverride: tenantSlug }
    : {};
}

function tenantKey(tenantSlug: string | null | undefined): string {
  if (tenantSlug === null) return "unset";
  if (typeof tenantSlug === "string") return tenantSlug;
  return "default";
}

export function useProjects(
  query?: {
    /** Admin-only: include archived projects. Server enforces. */
    includeArchived?: boolean;
    /** Admin-only: show only archived projects. Server enforces. */
    archivedOnly?: boolean;
  },
  options: { enabled?: boolean; tenantSlug?: string | null } = {},
) {
  const params = new URLSearchParams();
  if (query?.includeArchived) params.set("includeArchived", "true");
  if (query?.archivedOnly) params.set("archivedOnly", "true");
  const qs = params.toString();
  const tenantSlug = options.tenantSlug;
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), query],
    enabled: (options.enabled ?? true) && tenantSlug !== null,
    queryFn: () =>
      apiFetchUnchecked<ProjectListItem[]>(
        `/projects${qs ? `?${qs}` : ""}`,
        tenantOpts(tenantSlug),
      ),
  });
}

export function useProject(id: string, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), id],
    queryFn: () => apiFetchUnchecked<Project>(`/projects/${id}`, tenantOpts(tenantSlug)),
    enabled: !!id && tenantSlug !== null,
  });
}

/** CRM project linked to a knowledge map (`projects.map_id`), or null if none. */
export function useProjectByMapId(mapId: string | null | undefined, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), "by-map", mapId ?? ""],
    enabled: !!mapId && tenantSlug !== null,
    queryFn: () =>
      apiFetchUnchecked<Project | null>(`/projects/by-map/${mapId}`, tenantOpts(tenantSlug)),
  });
}

export function useCreateProject(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProjectCreateInput) =>
      apiFetchUnchecked<Project>("/projects", {
        method: "POST",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

export function useRelatedProjects(projectId: string, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "related"],
    enabled: !!projectId && tenantSlug !== null,
    queryFn: () =>
      apiFetchUnchecked<{ related: RelatedProject[] }>(
        `/projects/${projectId}/related`,
        tenantOpts(tenantSlug),
      ),
  });
}

export function useUpdateProject(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<ProjectCreate> & { id: string }) =>
      apiFetchUnchecked<Project>(`/projects/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), vars.id] });
    },
  });
}

/** Hard-delete a project. Admin only — server returns 403 otherwise. */
export function useDeleteProject(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<void>(`/projects/${id}`, {
        method: "DELETE",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/** Soft-delete (archive) a project — its tasks/updates are also hidden from non-admins. */
export function useArchiveProject(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<Project>(`/projects/${id}/archive`, {
        method: "POST",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/** Restore an archived project. Admin only. */
export function useUnarchiveProject(tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<Project>(`/projects/${id}/unarchive`, {
        method: "POST",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

// ─── Discovery briefs ─────────────────────────────────────

export function useProjectDiscoveryBriefs(
  projectId: string,
  query?: { includeArchived?: boolean; archivedOnly?: boolean },
  tenantSlug?: string | null,
) {
  const params = new URLSearchParams();
  if (query?.includeArchived) params.set("includeArchived", "true");
  if (query?.archivedOnly) params.set("archivedOnly", "true");
  const qs = params.toString();
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "discovery-briefs", query],
    queryFn: () =>
      apiFetchUnchecked<ProjectDiscoveryBriefListResponse>(
        `/projects/${projectId}/discovery-briefs${qs ? `?${qs}` : ""}`,
        tenantOpts(tenantSlug),
      ),
    enabled: !!projectId && tenantSlug !== null,
  });
}

export function useCreateProjectDiscoveryBrief(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProjectDiscoveryBriefCreate) =>
      apiFetchUnchecked<ProjectDiscoveryBrief>(`/projects/${projectId}/discovery-briefs`, {
        method: "POST",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "discovery-briefs"],
      }),
  });
}

export function useUpdateProjectDiscoveryBrief(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProjectDiscoveryBriefUpdate }) =>
      apiFetchUnchecked<ProjectDiscoveryBrief>(`/projects/${projectId}/discovery-briefs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "discovery-briefs"],
      }),
  });
}

export function useApproveProjectDiscoveryBrief(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<ProjectDiscoveryBrief>(
        `/projects/${projectId}/discovery-briefs/${id}/approve`,
        {
          method: "POST",
          ...tenantOpts(tenantSlug),
        },
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "discovery-briefs"],
      }),
  });
}

export function useArchiveProjectDiscoveryBrief(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<ProjectDiscoveryBrief>(
        `/projects/${projectId}/discovery-briefs/${id}/archive`,
        {
          method: "POST",
          ...tenantOpts(tenantSlug),
        },
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "discovery-briefs"],
      }),
  });
}

export function useUnarchiveProjectDiscoveryBrief(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<ProjectDiscoveryBrief>(
        `/projects/${projectId}/discovery-briefs/${id}/unarchive`,
        {
          method: "POST",
          ...tenantOpts(tenantSlug),
        },
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "discovery-briefs"],
      }),
  });
}

export function useDeleteProjectDiscoveryBrief(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<void>(`/projects/${projectId}/discovery-briefs/${id}`, {
        method: "DELETE",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "discovery-briefs"],
      }),
  });
}

// ─── Working documents ────────────────────────────────────

/** Ensure + return the project-linked workspace (find-or-create). */
export function useProjectWorkspace(projectId: string, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "workspace"],
    queryFn: () =>
      apiFetchUnchecked<Workspace>(`/projects/${projectId}/workspace`, tenantOpts(tenantSlug)),
    enabled: !!projectId && tenantSlug !== null,
  });
}

export function useProjectDocuments(projectId: string, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "documents"],
    queryFn: () =>
      apiFetchUnchecked<ProjectDocumentListResponse>(
        `/projects/${projectId}/documents`,
        tenantOpts(tenantSlug),
      ),
    enabled: !!projectId && tenantSlug !== null,
  });
}

export function useFinaliseProjectDocument(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: FinaliseProjectDocument) =>
      apiFetchUnchecked<ProjectDocument>(`/projects/${projectId}/finalise-working-document`, {
        method: "POST",
        body: JSON.stringify(args),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "documents"],
      });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

// ─── Tasks ─────────────────────────────────────────────────

export function useProjectTasks(
  projectId: string,
  query?: { category?: TaskCategory },
  tenantSlug?: string | null,
) {
  const params = new URLSearchParams();
  if (query?.category) params.set("category", query.category);
  const qs = params.toString();
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks", query ?? null],
    queryFn: () =>
      apiFetchUnchecked<Task[]>(
        `/projects/${projectId}/tasks${qs ? `?${qs}` : ""}`,
        tenantOpts(tenantSlug),
      ),
    enabled: !!projectId && tenantSlug !== null,
  });
}

export function useMyTasks() {
  return useQuery({
    queryKey: [...TASKS_KEY, "mine"],
    queryFn: () => apiFetchUnchecked<Task[]>(`/tasks/mine`),
  });
}

/**
 * Cross-project task aggregation for the dashboard "Tasks by category"
 * widget. One request returns counts + samples for all six canonical
 * categories. The server filters to open statuses only.
 */
export function useTasksByCategory() {
  return useQuery({
    queryKey: [...TASKS_KEY, "by-category"],
    queryFn: () => apiFetchUnchecked<TasksByCategoryResponse>(`/tasks/by-category`),
  });
}

export function useImportMapMilestones(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetchUnchecked<{ created: number; skipped: number }>(
        `/projects/${projectId}/import-map-milestones`,
        {
          method: "POST",
          ...tenantOpts(tenantSlug),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks"],
      });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "by-category"] });
    },
  });
}

export function useCreateTask(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<TaskCreate, "projectId">) =>
      apiFetchUnchecked<Task>(`/projects/${projectId}/tasks`, {
        method: "POST",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks"],
      });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      // Keep the dashboard "Tasks by category" counts/samples fresh.
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "by-category"] });
    },
  });
}

export function useUpdateTask(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<TaskCreate> & { id: string }) =>
      apiFetchUnchecked<Task>(`/projects/${projectId}/tasks/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks"],
      });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      // Keep the dashboard "Tasks by category" counts/samples fresh.
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "by-category"] });
    },
  });
}

export function moveTaskStatus({
  projectId,
  id,
  status,
  tenantSlug,
}: {
  projectId: string;
  id: string;
  status: string;
  tenantSlug?: string | null;
}) {
  return apiFetchUnchecked<Task>(`/projects/${projectId}/tasks/${id}/move`, {
    method: "POST",
    body: JSON.stringify({ status }),
    ...tenantOpts(tenantSlug),
  });
}

export function useMoveTask(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      moveTaskStatus({ projectId, id, status, tenantSlug }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks"],
      });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      // Keep the dashboard "Tasks by category" counts/samples fresh.
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "by-category"] });
    },
  });
}

export function useMoveMyTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: moveTaskStatus,
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      // Keep the dashboard "Tasks by category" counts/samples fresh.
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "by-category"] });
      qc.invalidateQueries({ queryKey: [...PROJECTS_KEY, task.projectId, "tasks"] });
    },
  });
}

export function useDeleteTask(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<void>(`/projects/${projectId}/tasks/${id}`, {
        method: "DELETE",
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks"],
      });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      // Keep the dashboard "Tasks by category" counts/samples fresh.
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "by-category"] });
    },
  });
}

export function useTaskComments(
  projectId: string,
  taskId: string | undefined,
  tenantSlug?: string | null,
) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks", taskId, "comments"],
    queryFn: () =>
      apiFetchUnchecked<{ comments: TaskComment[] }>(
        `/projects/${projectId}/tasks/${taskId}/comments`,
        tenantOpts(tenantSlug),
      ),
    enabled: !!projectId && !!taskId && tenantSlug !== null,
  });
}

export function useCreateTaskComment(
  projectId: string,
  taskId: string | undefined,
  tenantSlug?: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TaskCommentCreate) =>
      apiFetchUnchecked<TaskComment>(`/projects/${projectId}/tasks/${taskId}/comments`, {
        method: "POST",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "tasks", taskId, "comments"],
      });
    },
  });
}

// ─── Stakeholder updates ───────────────────────────────────

type StakeholderUpdate = {
  id: string;
  projectId: string;
  title: string;
  bodyHtml: string;
  summary: string | null;
  sentTo: string[];
  status: "draft" | "sent" | "failed";
  sentAt: string | null;
  createdAt: string;
};

export function useStakeholderUpdates(projectId: string, tenantSlug?: string | null) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "updates"],
    queryFn: () =>
      apiFetchUnchecked<StakeholderUpdate[]>(
        `/projects/${projectId}/updates`,
        tenantOpts(tenantSlug),
      ),
    enabled: !!projectId && tenantSlug !== null,
  });
}

export function useSendStakeholderUpdate(projectId: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<StakeholderUpdateCreate, "projectId">) =>
      apiFetchUnchecked<StakeholderUpdate>(`/projects/${projectId}/updates`, {
        method: "POST",
        body: JSON.stringify(data),
        ...tenantOpts(tenantSlug),
      }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: [...PROJECTS_KEY, tenantKey(tenantSlug), projectId, "updates"],
      }),
  });
}

export function useDraftStakeholderUpdate(projectId: string, tenantSlug?: string | null) {
  return useMutation({
    mutationFn: (toneHint?: string) =>
      apiFetchUnchecked<{
        title: string;
        bodyHtml: string;
        recipients: { email: string; name?: string }[];
      }>(`/projects/${projectId}/updates/draft`, {
        method: "POST",
        body: JSON.stringify({ toneHint: toneHint ?? "" }),
        ...tenantOpts(tenantSlug),
      }),
  });
}
